import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { Search, Trash2 } from "lucide-react";
import TileSelector, { TileOption } from "../TileSelector.tsx";
import {
  getTranslation,
  useSupportedInstruments,
} from "../../api/instrumentApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import LocationSearch from "./SearchBarLocationSearch.tsx";
import { Location, LocationType } from "../../api/locationApi.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { parseLocationType } from "../../api/teacherSearchApi.ts";
import { SearchModal } from "./SearchModal.tsx";
import { SearchBarPlaceholder } from "./SearchBarPlaceholder.tsx";

interface SearchBarProps {
  className?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({ className = "" }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { data: supportedInstruments } = useSupportedInstruments();
  const { language } = useLanguageStore();
  const { t } = useLingui();

  // Search state
  const [searchInstruments, setSearchInstruments] = useState<string[]>([]);
  const [latitude, setLatitude] = useState<number | undefined>(undefined);
  const [longitude, setLongitude] = useState<number | undefined>(undefined);
  const [radius, setRadius] = useState<number>(10);
  const [locationName, setLocationName] = useState<string>("");
  const [cityName, setCityName] = useState<string>("");
  const [locationType, setLocationType] = useState<LocationType | undefined>();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Initialize search state from URL params
  useEffect(() => {
    const instruments = searchParams.getAll("instrument");
    const longParam = searchParams.get("long");
    const latParam = searchParams.get("lat");
    const radiusParam = searchParams.get("radius");
    const locationTypeParam = searchParams.get("locationType");
    const locationNameParam = searchParams.get("locationName") || "";
    const cityNameParam = searchParams.get("cityName") || "";

    setSearchInstruments(instruments);
    setLongitude(longParam ? parseFloat(longParam) : undefined);
    setLatitude(latParam ? parseFloat(latParam) : undefined);
    setRadius(radiusParam ? parseInt(radiusParam) : 10);
    setLocationName(locationNameParam);
    setCityName(cityNameParam);
    if (locationTypeParam != null) {
      const parsedLocationType = parseLocationType(locationTypeParam);
      setLocationType(
        parsedLocationType != null ? parsedLocationType : undefined,
      );
    }
  }, [searchParams]);

  // Handle search execution
  const handleSearch = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete("languages");
    params.delete("instrument");
    searchInstruments.forEach((instrument) =>
      params.append("instrument", instrument),
    );
    if (locationType) {
      params.set("locationType", locationType);
    } else {
      params.delete("locationType");
    }
    if (longitude !== undefined && latitude !== undefined && radius) {
      params.set("long", String(longitude));
      params.set("lat", String(latitude));
      params.set("radius", radius.toString());
      if (locationName) {
        params.set("locationName", locationName);
      }
      if (cityName) {
        params.set("cityName", cityName);
      }
    } else {
      params.delete("long");
      params.delete("lat");
      params.delete("radius");
      params.delete("locationName");
      params.delete("cityName");
    }

    navigate(`/search?${params.toString()}`);
    setIsOpen(false);
  }, [
    cityName,
    latitude,
    locationName,
    locationType,
    longitude,
    navigate,
    radius,
    searchInstruments,
    searchParams,
  ]);

  const instrumentOptions: TileOption[] = useMemo(
    () =>
      supportedInstruments?.map((instrument) => ({
        value: instrument.name,
        label: getTranslation(instrument, language),
        icon: "Music",
      })) || [],
    [language, supportedInstruments],
  );

  const locationTypeOptions: TileOption[] = [
    {
      label: t`In Person`,
      value: "IN_PERSON",
      icon: "House",
    },
    {
      label: t`Remote`,
      value: "REMOTE",
      icon: "MonitorPlay",
    },
  ];

  const getSearchBarPlaceholder = () => {
    const instrument = supportedInstruments?.find(
      (value) => value.name === searchInstruments[0],
    );
    const translatedInstrument = instrument
      ? getTranslation(instrument, language)
      : undefined;
    const translatedLocationType = locationTypeOptions.find(
      (value) => value.value === locationType,
    )?.label;
    const locationLabel = cityName || undefined;
    return (
      <SearchBarPlaceholder
        translatedInstrument={translatedInstrument}
        translatedLessonFormat={translatedLocationType}
        locationLabel={locationLabel}
      />
    );
  };

  const isLocationSet = useMemo(() => {
    return (
      longitude !== undefined && latitude !== undefined && locationName !== ""
    );
  }, [latitude, locationName, longitude]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && !(event.target as Element).closest(".search-panel")) {
        if (
          searchInstruments.length > 0 ||
          latitude !== undefined ||
          longitude !== undefined ||
          locationName !== ""
        ) {
          handleSearch();
        }
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [
    handleSearch,
    isOpen,
    latitude,
    locationName,
    longitude,
    searchInstruments.length,
  ]);

  const buildLocationString = (location: Location) => {
    return [
      `${location.streetName || ""} ${location.houseNumber || ""}`.trim(),
      `${location.postalCode || ""} ${location.city || ""}`.trim(),
      location.countryCode,
    ]
      .filter(Boolean)
      .join(", ");
  };

  const handleLocationSelect = (location: Location) => {
    if (location.latitude && location.longitude) {
      setLatitude(location.latitude);
      setLongitude(location.longitude);
      setLocationName(buildLocationString(location));
      setCityName(location.city || "");
    }
  };

  const resetSearch = () => {
    if (pathname !== "/search") {
      setIsOpen(false);
      return;
    }
    const newParams = new URLSearchParams(searchParams.toString());
    const keysToRemove = [
      "instrument",
      "locationType",
      "long",
      "lat",
      "radius",
      "filters",
      "locationName",
      "cityName",
    ];
    keysToRemove.forEach((key) => newParams.delete(key));
    setSearchInstruments([]);
    setLatitude(undefined);
    setLongitude(undefined);
    setRadius(10);
    setLocationType(undefined);
    setLocationName("");
    setCityName("");

    const queryString = newParams.toString();
    navigate(`${pathname}${queryString ? `?${queryString}` : ""}`);

    setIsOpen(false);
  };

  return (
    <div className={`${className}`}>
      {/* Main Search Button */}
      <button
        className={`border-soluud-border relative bg-white ${isOpen ? "z-50" : ""} flex h-11 w-full min-w-12 cursor-pointer items-center rounded-full border px-3 shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-[--shadow-card-hover] sm:w-64 sm:px-4 md:w-[28rem]`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-label={t`Open search filters`}
      >
        <div className="flex w-full min-w-0 items-center">
          <Search size={18} className="shrink-0" aria-hidden="true" />
          {getSearchBarPlaceholder()}
        </div>
      </button>

      {/* Search Panel */}
      {isOpen && (
        <>
          <SearchModal
            handleSearch={handleSearch}
            searchButtonText={t`Search Teachers`}
            modalIdentifier={"search-panel"}
          >
            <>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-soluud-text-primary text-sm font-semibold">
                  <Trans>Instruments to learn</Trans>
                </label>
                <button
                  className={
                    "bg-soluud-background flex h-12 w-12 cursor-pointer items-center justify-center rounded-2xl"
                  }
                  onClick={resetSearch}
                  title={t`Reset search`}
                  aria-label={t`Reset search`}
                >
                  <Trash2 size={20} aria-hidden="true" />
                </button>
              </div>
              {instrumentOptions.length > 0 ? (
                <TileSelector
                  options={instrumentOptions}
                  selectedValues={searchInstruments}
                  onChange={setSearchInstruments}
                  multiSelect={false}
                />
              ) : (
                <label className="text-soluud-text-primary ml-2 text-sm">
                  <Trans>No instrument to select.</Trans>
                </label>
              )}

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <label className="text-soluud-text-primary text-sm font-semibold">
                    <Trans>Lesson format</Trans>
                  </label>
                </div>

                <TileSelector
                  options={locationTypeOptions}
                  selectedValues={locationType ? [locationType] : []}
                  onChange={(newValues) =>
                    setLocationType(newValues[0] as LocationType)
                  }
                  multiSelect={false}
                />
                <div className="space-y-2 pt-2">
                  <div className="flex items-center space-x-2">
                    <label className="text-soluud-text-primary text-sm font-semibold">
                      <Trans>Location</Trans>
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <LocationSearch
                        onLocationSelect={handleLocationSelect}
                        initialValue={locationName}
                      />
                    </div>
                    {isLocationSet && (
                      <button
                        className="bg-soluud-background flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-gray-200 transition-colors hover:bg-gray-100"
                        onClick={() => {
                          setLatitude(undefined);
                          setLongitude(undefined);
                          setLocationName("");
                          setCityName("");
                        }}
                        title={t`Clear location`}
                        aria-label={t`Clear location`}
                      >
                        <Trash2
                          size={18}
                          className="text-gray-500"
                          aria-hidden="true"
                        />
                      </button>
                    )}
                  </div>
                  {isLocationSet && (
                    <div className="space-y-2">
                      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-4">
                        <label className="text-soluud-text-primary/70 text-xs font-medium lg:whitespace-nowrap">
                          <Trans>Search radius</Trans>
                        </label>
                        <div className="flex flex-1 items-center gap-3">
                          <span className="text-soluud-text-primary/50 hidden text-xs lg:inline">
                            5 km
                          </span>
                          <input
                            type="range"
                            min="5"
                            max="100"
                            step="5"
                            value={radius}
                            onChange={(e) =>
                              setRadius(parseInt(e.target.value))
                            }
                            className="bg-soluud-primary/20 [&::-webkit-slider-thumb]:bg-soluud-primary h-2 flex-1 cursor-pointer appearance-none rounded-full [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110"
                          />
                          <span className="text-soluud-text-primary/50 hidden text-xs lg:inline">
                            100 km
                          </span>
                          <span className="text-soluud-primary text-sm font-semibold lg:min-w-[3.5rem] lg:text-right">
                            {radius} km
                          </span>
                        </div>
                      </div>
                      <div className="text-soluud-text-primary/50 flex justify-between text-xs lg:hidden">
                        <span>5 km</span>
                        <span>100 km</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          </SearchModal>
        </>
      )}
    </div>
  );
};

export default SearchBar;
